import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';

import { Observable, of } from 'rxjs';
import { map, skipWhile, take } from 'rxjs/operators';

import { ActiveSecurityContextStateService } from '../active-security/active-security-context.state-service';
import { ActiveAuthenticationStateService } from '@auth-n/state/active-authentication.state-service';
import { DomainRedirectService } from '@domain/domain-redirect.service';
import { LoggingService } from '@logging/logging.service';

/**
 * Prevents users accessing views that are for unauthenticated users only e.g. the login form.
 * Redirects to the dashboard if accessed.
 */
@Injectable({ providedIn: 'root' })
export class AnonymousRouteGuard {
  constructor(
    private router: Router,
    private authenticationState: ActiveAuthenticationStateService,
    private activeSecurityState: ActiveSecurityContextStateService,
    private domainRedirect: DomainRedirectService,
    private logger: LoggingService
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    if (this.domainRedirect.isRedirectInProgress) {
      this.logger.trace('ANONYMOUS ROUTE GUARD: cross-domain redirect in progress, blocking navigation');
      return of(false);
    }

    return this.activeSecurityState.fetchingActiveUser$.pipe(
      skipWhile((transitioning) => transitioning !== false),
      take(1),
      map((transitioning) => {
        this.logger.trace('ANONYMOUS ROUTE GUARD: transitioning?', transitioning);
        this.logger.trace('ANONYMOUS ROUTE GUARD: isAuthenticated?', this.authenticationState.isAuthenticated);
        this.logger.trace(
          'ANONYMOUS ROUTE GUARD: state.url:',
          state.url,
          'window.location.href:',
          window.location.href,
          'hostname:',
          window.location.hostname,
          'search:',
          window.location.search,
          'activeIdentity:',
          this.authenticationState.isAuthenticated ? 'set' : 'undefined'
        );
        if (this.authenticationState.isAuthenticated) {
          this.activeSecurityState.redirectToBestRoute();
          return false;
        }
        return true;
      })
    );
  }
}
